import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { partnerApi } from "../../services/partnerApi";
import { statusBadgeClass } from "../../components/instructor/InstructorShell";
import BrandMark from "../../components/BrandMark";
import Seo from "../../components/Seo";
import { useAuth } from "../../context/AuthContext";

export default function AdminPartnerApplicationsPage() {
  const { signOut, user } = useAuth();
  const [status, setStatus] = useState("");
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const data = await partnerApi.adminList(status || undefined);
        if (!cancelled) setApplications(data.applications || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [status]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="Admin Partner Applications"
        description="Review partner applications."
        canonicalPath="/admin/partner-applications"
        robots="noindex,nofollow"
      />
      <header className="border-b border-gray-100 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <BrandMark />
            <span className="font-semibold text-gray-900">Admin · Partner applications</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/admin/applications" className="text-emerald-700 hover:underline">
              Instructors
            </Link>
            <Link to="/partner" className="text-emerald-700 hover:underline">
              Partner
            </Link>
            <span className="text-gray-500">{user?.email}</span>
            <button type="button" onClick={() => signOut()} className="text-gray-600">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-gray-900">Partner review queue</h1>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="">All actionable</option>
            <option value="Submitted">Submitted</option>
            <option value="UnderReview">Under Review</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Suspended">Suspended</option>
          </select>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-gray-100 bg-slate-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Scrape</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => (
                  <tr key={app.id} className="border-b border-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {app.trade_name || app.legal_name}
                      </div>
                      <div className="text-xs text-gray-500">{app.email}</div>
                      <div className="truncate text-xs text-emerald-700">{app.website}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(app.status)}`}
                      >
                        {app.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {app.primary_scrape_status || "—"}
                      {app.ready_source_count > 0 ? (
                        <span className="ml-1 text-xs text-emerald-700">
                          ({app.ready_source_count} ready)
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">{app.completeness_score}%</td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/partner-applications/${app.id}`}
                        className="font-medium text-emerald-700 hover:underline"
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
                {!applications.length && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      No partner applications
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
